import { Link } from 'react-router-dom';
import Hero from '@/pages/home/components/Hero';
import RankingSection from '@/pages/home/components/RankingSection';
import CategorySection from '@/pages/home/components/CategorySection';
import FaqSection from '@/pages/home/components/FaqSection';
import ComicCard from '@/components/feature/ComicCard';
import JsonLd from '@/components/feature/JsonLd';
import { useSeo, SITE_URL } from '@/hooks/useSeo';
import { comics } from '@/mocks/comics';
import { chaptersByComic } from '@/mocks/chapters';

const faqData = [
  {
    q: '《帐篷里的秘密》漫画在哪里可以免费阅读？',
    a: '打开5177漫画网首页，点击"立即免费阅读"按钮即可进入《帐篷里的秘密》漫画详情页，支持从第1话开始在线阅读全集中文故事，完全免费，无需注册。',
  },
  {
    q: '5177漫画网的韩漫需要付费吗？',
    a: '不需要。5177漫画网全站韩国漫画均免费阅读，包括《帐篷里的秘密》《温柔禁区》等热门韩漫作品，所有章节均可直接在线浏览，无任何隐藏收费。',
  },
  {
    q: '漫画多久更新一次？',
    a: '热门连载作品每日更新，一般于每日凌晨同步最新话；完结作品已收录全部章节，随时可以一口气看完。',
  },
  {
    q: '如何在手机上阅读漫画？',
    a: '5177漫画网支持手机、平板、电脑等多端访问，直接使用浏览器打开即可自适应阅读。',
  },
  {
    q: '漫画可以下载离线观看吗？',
    a: '当前为在线免费阅读模式，建议在有网络的环境下浏览，收藏到浏览器书签后可以快速回到上次阅读的位置。',
  },
  {
    q: '遇到漫画打不开怎么办？',
    a: '可以先尝试刷新页面或更换浏览器，若问题依旧可通过页面底部的邮箱与我们联系，我们会第一时间处理并反馈。',
  },
];

const webPageLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/#webpage`,
  url: `${SITE_URL}/`,
  name: '《帐篷里的秘密》漫画-免费阅读看韩国漫画-5177漫画网',
  description: '来5177漫画网免费阅读《帐篷里的秘密》韩国漫画，在线浏览全集中文故事，享受免费且精彩的韩漫体验。',
  inLanguage: 'zh-CN',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  about: { '@id': `${SITE_URL}/#organization` },
  mainEntity: {
    '@type': 'FAQPage',
    mainEntity: faqData.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  },
};

export default function HomePage() {
  useSeo({
    title: '《帐篷里的秘密》漫画-免费阅读看韩国漫画-5177漫画网',
    description: '来5177漫画网免费阅读《帐篷里的秘密》韩国漫画，在线浏览全集中文故事，享受免费且精彩的韩漫体验，随时沉浸其中。',
    keywords: '《帐篷里的秘密》漫画,免费阅读,韩国漫画,韩漫,5177漫画网',
    ogType: 'website',
    canonicalPath: '/',
  });

  const latestComics = [...comics].sort((a, b) => {
    const da = chaptersByComic[a.id]?.[0]?.updatedAt ?? '';
    const db = chaptersByComic[b.id]?.[0]?.updatedAt ?? '';
    return db.localeCompare(da);
  });
  const latestGrid = latestComics.slice(0, 8);
  const ratingGrid = [...comics].sort((a, b) => b.rating - a.rating).slice(0, 8);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={webPageLd} />

      <Hero />

      <main>
        <RankingSection />
        <CategorySection />

        <section className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <div className="flex items-end justify-between">
            <h2 className="flex items-center gap-2 font-display text-2xl md:text-3xl">
              <span className="text-primary-500">
                <i className="ri-time-line text-2xl md:text-3xl" aria-hidden="true" />
              </span>
              最新更新
            </h2>
            <Link
              to="/category/all"
              className="text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              查看全部
              <i className="ri-arrow-right-line ml-1" aria-hidden="true" />
            </Link>
          </div>
          <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {latestGrid.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </section>

        <section className="bg-background-100 py-12 md:py-16">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="flex items-end justify-between">
              <h2 className="flex items-center gap-2 font-display text-2xl md:text-3xl">
                <span className="text-accent-500">
                  <i className="ri-medal-fill text-2xl md:text-3xl" aria-hidden="true" />
                </span>
                高分佳作
              </h2>
              <Link
                to="/category/all"
                className="text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                查看更多
                <i className="ri-arrow-right-line ml-1" aria-hidden="true" />
              </Link>
            </div>
            <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {ratingGrid.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        <FaqSection />

        <section className="mx-auto max-w-7xl px-4 md:px-6 pb-4 pt-2">
          <div className="relative overflow-hidden rounded-lg bg-primary-600">
            <div className="absolute -right-10 -top-16 h-56 w-56 rounded-full bg-accent-500/30 blur-3xl" />
            <div className="absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-primary-400/40 blur-3xl" />
            <div className="relative flex flex-col lg:flex-row items-center justify-between gap-6 px-6 md:px-12 py-10 md:py-12">
              <div className="text-center lg:text-left">
                <h2 className="font-display text-2xl md:text-3xl text-background-50">
                  随时随地，畅享免费韩漫
                </h2>
                <p className="mt-2 text-sm md:text-base text-primary-100">
                  无需注册 · 无弹窗广告 · 全集中文翻译，立刻开启你的漫画之旅
                </p>
              </div>
              <Link
                to="/comic/tent-secret"
                className="inline-flex items-center gap-2 rounded-md bg-background-50 px-6 py-3 text-sm font-medium text-primary-700 whitespace-nowrap transition-colors hover:bg-accent-100"
              >
                <i className="ri-book-open-line text-base" aria-hidden="true" />
                立即阅读《帐篷里的秘密》
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}